import { Coachmark, TeachingBubbleContent } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import { Link } from '@material-ui/core';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Fab, Grid, Tooltip, Typography } from '@material-ui/core';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import { curveCardinal } from '@visx/curve';
import { GridColumns, GridRows } from '@visx/grid';
import { Group } from '@visx/group';
import { scaleLinear, scaleTime } from '@visx/scale';
import { Line, LinePath } from '@visx/shape';
import React, { useEffect, useState } from "react";
import { Motion, spring } from 'react-motion';
import { BaseColor, DataFilters, LargeColor, ResidentialColor, SettingChoices, SMEColor, StepOrder, StoryLims } from "../consts";
import { getDailyChangeData, getKeyDateData, getRawYearData, getRawYearMarketsData, getYoYMarketData, getYoYMarketRegionData, getYoYRegionData, getYoYTotalData } from '../Data/DataFunctions';
import { DailyChange, KeyDate, RawYearComparison, RawYearMarkets, YoYMarket, YoYMarketRegion, YoYRegion, YoYTotal } from '../Data/DataTypes';
import { AnchorPost, BubbleDirection, getStepNumber, StoryToolTipText } from '../Data/StoryFunctions';
import '../styles.css';
import { KeyDateCalendar } from './Calendar';
import { OneYearAxis, TwoYearAxis } from './CustomAxis';
import { Intro } from './Intro';
import { CustomLegend } from './Legend';
import SideBar from './SideBar';


export function MainGraph() {
    const [xScale, setxScale] = useState<any>(undefined)
    const [yScale, setyScale] = useState<any>(undefined)
    const [CurrentStep, SetCurrentStep] = useState<SettingChoices>("YoYTotal")
    const [yAxisLabel, setyLabel] = useState<string>("")
    const [showDialog, setShowDialog] = useState<boolean>(false);

    const [RawConsumption, SetRawConsumption] = useState<RawYearComparison[]>([])
    const [RawConsumptionMarkets, SetRawConsumptionMarkets] = useState<RawYearMarkets[]>([])
    const [YoYData, SetYoYData] = useState<YoYTotal[]>([])
    const [YoYMarketData, SetYoYMarketData] = useState<YoYMarket[]>([])
    const [YoYRegionData, SetYoYRegionData] = useState<YoYRegion[]>([])
    const [YoYMarketRegionData, SetYoYMarketRegionData] = useState<YoYMarketRegion[]>([])
    const [DailyChangeData, SetDailyChangeData] = useState<DailyChange[]>([])
    const [KeyDateData, SetKeyDateData] = useState<KeyDate[]>([])
    const [StepReset, setStepReset] = useState<Boolean>(false)

    const [ActiveFilters, SetFilters] = useState<DataFilters[]>([])

    const canvasHeight = document.documentElement.clientHeight * 0.90;
    const canvasWidth = document.documentElement.clientWidth * 0.80;
    const graphHeight = document.documentElement.clientHeight * 0.80;
    const graphWidth = document.documentElement.clientWidth * 0.75;
    const leftPad = document.documentElement.clientWidth * 0.05;

    //Story Variables
    const [storyStep, setStoryStep] = useState<number>(1)
    const [CurrStoryLimit, SetStorylim] = useState<number>(StoryLims["Intro"])
    const [ChartTitle, setTitle] = useState<string>("Total Consumption")

    // Ref targets
    const PointOne = React.useRef<HTMLDivElement>(null);
    const [isCoachmark1Visible, { setFalse: hideCoachmark1, setTrue: showCoachmark1 }] = useBoolean(false);
    const PointTwo = React.useRef<HTMLDivElement>(null);
    const [isCoachmark2Visible, { setFalse: hideCoachmark2, setTrue: showCoachmark2 }] = useBoolean(false);
    const PointThree = React.useRef<HTMLDivElement>(null);
    const [isCoachmark3Visible, { setFalse: hideCoachmark3, setTrue: showCoachmark3 }] = useBoolean(false);

    // Step Functions
    function handleStep() {
        if (CurrentStep === "YoYMarketRegion" && ActiveFilters.includes("Markets") && !showDialog) {
            setShowDialog(true)
            hideCoachmark1();
            hideCoachmark2();
            hideCoachmark3();
        }
        else if (CurrentStep !== "Calendar") {
            setStepandScale(StepOrder[getStepNumber(CurrentStep) + 1])
        }
    }

    // Tooltip positioning
    function getTTPos(s: number) {
        return (
            {
                directionalHint: BubbleDirection(CurrentStep, s, ActiveFilters.includes("Markets")),
                doNotLayer: false,
            }
        )
    }

    // Set Filters
    function updateFilters(newFilter: DataFilters, reset = false) {
        if (reset) {
            SetFilters([])
        }
        else {
            setStepReset(true)
            SetFilters([newFilter])
            setTitle(detTitle(CurrentStep, true))
        }
    }

    function activateMTFilter() {
        hideCoachmark1()
        hideCoachmark2()
        hideCoachmark3()
        updateFilters("Markets")
    }

    function handleDialogClose() {
        handleStep();
        setShowDialog(false);
    };

    // Axis Functions
    function setDateValueScale(minDate: number, maxDate: number, minVal: number, maxVal: number) {
        setxScale(() => scaleTime<number>({ domain: [minDate, maxDate], nice: true, range: [leftPad, graphWidth] }))
        setyScale(() => scaleLinear<number>({ domain: [maxVal, minVal], nice: true, range: [50, graphHeight] }))
    }

    function setStepandScale(newStep: SettingChoices) {
        SetCurrentStep(newStep);
        hideCoachmark1();
        hideCoachmark2();
        hideCoachmark3();
        setStepReset(true)
        updateFilters("2019", false)
        setTitle(detTitle(newStep))
        SetStorylim(StoryLims[newStep])
        if (newStep === "RawYearComparison") {
            let minDate = Math.min(...RawConsumption.map((d) => { return d.Month.valueOf() }))
            let maxDate = Math.max(...RawConsumption.map((d) => { return d.Month.valueOf() }))
            let maxVal = Math.max(...RawConsumption.map((d) => { return d.Consumption }))
            setDateValueScale(minDate, maxDate, 0, maxVal)
            setyLabel("Demand (MWh)")
        }
        else if (newStep === "YoYTotal") {
            let minDate = Math.min(...YoYMarketData.map((d) => { return d.Month.valueOf() }))
            let maxDate = Math.max(...YoYMarketData.map((d) => { return d.Month.valueOf() }))
            let minVal = Math.min(...YoYMarketData.map((d) => { return d.YoYChange }))
            let maxVal = Math.max(...YoYMarketData.map((d) => { return d.YoYChange }))
            setDateValueScale(minDate, maxDate, minVal, maxVal)
            setyLabel("Year on Year Change (%)")
        }
        else if (newStep === "YoYMarketRegion") {
            let minDate = Math.min(...YoYMarketRegionData.map((d) => { return d.Month.valueOf() }))
            let maxDate = Math.max(...YoYMarketRegionData.map((d) => { return d.Month.valueOf() }))
            let minVal = Math.min(...YoYMarketRegionData.map((d) => { return d.YoYChange }))
            let maxVal = Math.max(...YoYMarketRegionData.map((d) => { return d.YoYChange }))
            setyLabel("Year on Year Change (%)")
            setDateValueScale(minDate, maxDate, minVal, maxVal)
        }
        else if (newStep === "Intro") {

        }
        if (newStep !== CurrentStep) {
            setStoryStep(1);
        }
    }

    function detTitle(setting: SettingChoices, marketFilter = false): string {
        if (setting === "YoYTotal") {
            if (marketFilter) {
                return "Electricity Demand across Victoria by Market Type"
            }
            else {
                return "Electricity Demand across Victoria"
            }
        }
        else if (setting === "RawYearComparison") {
            if (marketFilter) {
                return "Year on Year Change in Demand by Market Type"
            }
            else {
                return "Year on Year Change in Demand"
            }
        }
        else if (setting === "YoYMarketRegion") {
            if (marketFilter) {
                return "Year on Year Change in Demand by Region"
            }
            else {
                return "Year on Year Change in Demand by Region and Market Type"
            }
        }
        else {
            return ""
        }
    }

    useEffect(() => {
        if (RawConsumption.length === 0) {
            console.log("Loading Data")
            getRawYearData(SetRawConsumption);
            getRawYearMarketsData(SetRawConsumptionMarkets);
            getYoYTotalData(SetYoYData);
            getYoYMarketData(SetYoYMarketData)
            getYoYRegionData(SetYoYRegionData)
            getYoYMarketRegionData(SetYoYMarketRegionData)
            getDailyChangeData(SetDailyChangeData)
            getKeyDateData(SetKeyDateData)
        }
        else if (xScale === undefined && storyStep === 1 && CurrentStep !== "Intro") {
            console.log("Data Loaded")
            setStepandScale("Intro")
        }
        if (!isCoachmark1Visible && StepReset) {
            showCoachmark1()
            showCoachmark2()
            showCoachmark3()
            setStepReset(false)
        }
    })

    let xAxisSlide: any = (x: number, visible: boolean) => {
        return (
            {
                WebkitTransform: `translate3d(${x}px, 0, 0)`,
                transform: `translate3d(${x}px, 0, 0)`,
                visibility: (visible ? 'visible' : 'hidden')
            }
        )
    }

    const leftColStyle = {
        borderRight: '1px solid rgba(0, 0, 0, 0.05)'
    }

    const lineOpacity = ActiveFilters.includes("Markets") ? 0 : 1

    return (
        <Grid container>
            <Group id="anchors">
                <span id="InfoAnchor1" style={AnchorPost(CurrentStep, 1, ActiveFilters.includes("Markets"))} ref={PointOne} />
                <span id="InfoAnchor2" style={AnchorPost(CurrentStep, 2, ActiveFilters.includes("Markets"))} ref={PointTwo} />
                <span id="InfoAnchor3" style={AnchorPost(CurrentStep, 3, ActiveFilters.includes("Markets"))} ref={PointThree} />
            </Group>

            <Group id="LegendContainer">
                <CustomLegend currStep={CurrentStep} marketsActive={ActiveFilters.includes("Markets")} updateFilters={activateMTFilter} />
            </Group>

            <Group id="infoBoxes">
                {(CurrentStep !== "Intro") && (
                    <>
                        {(CurrStoryLimit >= 1 && isCoachmark1Visible) && (
                            <Coachmark target={PointOne.current} positioningContainerProps={getTTPos(1)} delayBeforeCoachmarkAnimation={1000}>
                                <TeachingBubbleContent onDismiss={hideCoachmark1} hasCloseButton>
                                    {StoryToolTipText(1, CurrentStep, ActiveFilters.includes("Markets"))}
                                </TeachingBubbleContent>
                            </Coachmark>
                        )}

                        {(CurrStoryLimit >= 2 && isCoachmark2Visible) && (
                            <Coachmark target={PointTwo.current} positioningContainerProps={getTTPos(2)} delayBeforeCoachmarkAnimation={1000} >
                                <TeachingBubbleContent onDismiss={hideCoachmark2} hasCloseButton>
                                    {StoryToolTipText(2, CurrentStep, ActiveFilters.includes("Markets"))}
                                </TeachingBubbleContent>
                            </Coachmark>
                        )}
                        {(CurrStoryLimit >= 3 && isCoachmark3Visible) && (
                            <Coachmark target={PointThree.current} positioningContainerProps={getTTPos(3)} delayBeforeCoachmarkAnimation={1000}>
                                <TeachingBubbleContent onDismiss={hideCoachmark3} hasCloseButton>
                                    {StoryToolTipText(3, CurrentStep, ActiveFilters.includes("Markets"))}
                                </TeachingBubbleContent>
                            </Coachmark>
                        )}
                    </>
                )}
            </Group>

            <Grid item xs={2} style={leftColStyle}>
                <SideBar currStep={CurrentStep} updateStep={setStepandScale} updateFilters={activateMTFilter} />
            </Grid>
            <Grid item xs={10}>
                {CurrentStep === "Intro" && <Intro />}

                {(CurrentStep !== "Intro" && CurrentStep !== "Calendar" && xScale) &&
                    <Group id="CanvasContainer" style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                        <Typography variant="h6">{ChartTitle}</Typography>
                        <svg height={canvasHeight} width={canvasWidth} id="Canvas">

                            <Group id="Axis">
                                {CurrentStep !== "RawYearComparison" ? OneYearAxis(graphHeight, leftPad, graphWidth, xScale, yScale, yAxisLabel) : TwoYearAxis(graphHeight, leftPad, graphWidth, xScale, yScale, yAxisLabel)}
                                <GridRows scale={yScale} width={graphWidth - leftPad} height={graphHeight} stroke="#e0e0e0" left={leftPad} />
                                <GridColumns scale={xScale} width={graphWidth} height={graphHeight - 50} stroke="#e0e0e0" top={50} />
                            </Group>

                            {/* Total Consumption Lines */}
                            <Group id={"TotalConsumpion"}>
                                <Motion style={{ motionVar: spring((CurrentStep === "RawYearComparison") ? 0 : 400) }}>
                                    {({ motionVar }) =>
                                        <LinePath
                                            data={RawConsumption}
                                            curve={curveCardinal}
                                            x={d => xScale(d.Month.valueOf())}
                                            y={d => yScale(d.Consumption)}
                                            stroke={BaseColor}
                                            strokeWidth={1.5}
                                            style={xAxisSlide(motionVar, (CurrentStep === "RawYearComparison"))}
                                            className={"lineStyle"}
                                        />}
                                </Motion>
                                <Motion style={{ motionVar: spring((CurrentStep === "RawYearComparison" && ActiveFilters.includes("Markets")) ? 1 : 0) }}>
                                    {({ motionVar }) =>
                                        <Group id="RawYearMarkets">
                                            <LinePath
                                                data={RawConsumptionMarkets.filter((d: RawYearMarkets) => { return d.Market === "Commercial" })}
                                                curve={curveCardinal}
                                                x={d => xScale(d.Month.valueOf())}
                                                y={d => yScale(d.Consumption)}
                                                stroke={SMEColor}
                                                strokeWidth={1.5}
                                                opacity={motionVar}
                                                className={"lineStyle"}
                                            />
                                            <LinePath
                                                data={RawConsumptionMarkets.filter((d: RawYearMarkets) => { return d.Market === "Large" })}
                                                curve={curveCardinal}
                                                x={d => xScale(d.Month.valueOf())}
                                                y={d => yScale(d.Consumption)}
                                                stroke={LargeColor}
                                                strokeWidth={1.5}
                                                opacity={motionVar}
                                                className={"lineStyle"}
                                            />
                                            <LinePath
                                                data={RawConsumptionMarkets.filter((d: RawYearMarkets) => { return d.Market === "Residential" })}
                                                curve={curveCardinal}
                                                x={d => xScale(d.Month.valueOf())}
                                                y={d => yScale(d.Consumption)}
                                                stroke={ResidentialColor}
                                                strokeWidth={1.5}
                                                opacity={motionVar}
                                                className={"lineStyle"}
                                            />
                                        </Group>
                                    }
                                </Motion>
                            </Group>

                            {/* YoY & YoYMarket Lines */}
                            <Group id={"YoYChange"}>
                                <Motion style={{ motionVar: spring((CurrentStep === "YoYTotal") ? 0 : 400) }}>
                                    {({ motionVar }) =>
                                        <Group>
                                            <Line from={{ x: xScale(new Date(2020, 0, 1)), y: yScale(0) }} to={{ x: graphWidth, y: yScale(0) }} stroke={BaseColor} strokeWidth={1.5} style={xAxisSlide(motionVar, (CurrentStep === "YoYTotal"))} />
                                            <LinePath
                                                data={YoYData}
                                                curve={curveCardinal}
                                                x={d => xScale(d.Month.valueOf())}
                                                y={d => yScale(d.YoYChange)}
                                                stroke={BaseColor}
                                                strokeWidth={1.5}
                                                style={xAxisSlide(motionVar, (CurrentStep === "YoYTotal"))}
                                                className={"lineStyle"}
                                                opacity={lineOpacity}
                                            />
                                        </Group>}
                                </Motion>

                                <Motion style={{ motionVar: spring((CurrentStep === "YoYTotal" && ActiveFilters.includes("Markets")) ? 1 : 0) }}>
                                    {({ motionVar }) =>
                                        <Group id="YoYChangeMarkets">
                                            <LinePath
                                                data={YoYMarketData.filter((d: YoYMarket) => { return d.Market === "Commercial" })}
                                                curve={curveCardinal}
                                                x={d => xScale(d.Month.valueOf())}
                                                y={d => yScale(d.YoYChange)}
                                                stroke={SMEColor}
                                                strokeWidth={1.5}
                                                opacity={motionVar}
                                                className={"lineStyle"}
                                            />
                                            <LinePath
                                                data={YoYMarketData.filter((d: YoYMarket) => { return d.Market === "Large" })}
                                                curve={curveCardinal}
                                                x={d => xScale(d.Month.valueOf())}
                                                y={d => yScale(d.YoYChange)}
                                                stroke={LargeColor}
                                                strokeWidth={1.5}
                                                opacity={motionVar}
                                                className={"lineStyle"}
                                            />
                                            <LinePath
                                                data={YoYMarketData.filter((d: YoYMarket) => { return d.Market === "Residential" })}
                                                curve={curveCardinal}
                                                x={d => xScale(d.Month.valueOf())}
                                                y={d => yScale(d.YoYChange)}
                                                stroke={ResidentialColor}
                                                strokeWidth={1.5}
                                                opacity={motionVar}
                                                className={"lineStyle"}
                                            />
                                        </Group>
                                    }
                                </Motion>
                            </Group>

                            {/* YoY Region and Yoy Market+Region Lines */}
                            <Group id={"YoYRegionChange"}>
                                <Motion style={{ motionVar: spring((CurrentStep === "YoYMarketRegion") ? 0 : 400) }}>
                                    {({ motionVar }) =>
                                        <Group id="YoYChangeRegion">
                                            <Line from={{ x: xScale(new Date(2020, 0, 1)), y: yScale(0) }} to={{ x: graphWidth, y: yScale(0) }} stroke={BaseColor} strokeWidth={1.5} style={xAxisSlide(motionVar, (CurrentStep === "YoYMarketRegion"))} />
                                            <LinePath
                                                data={YoYRegionData.filter((d: YoYRegion) => { return d.Region === "Regional" })}
                                                curve={curveCardinal}
                                                x={d => xScale(d.Month.valueOf())}
                                                y={d => yScale(d.YoYChange)}
                                                stroke={BaseColor}
                                                strokeWidth={1.5}
                                                strokeDasharray={"5, 3"}
                                                style={xAxisSlide(motionVar, (CurrentStep === "YoYMarketRegion"))}
                                                className={"lineStyle"}
                                                opacity={lineOpacity}
                                            />
                                            <LinePath
                                                data={YoYRegionData.filter((d: YoYRegion) => { return d.Region === "Metropolitan" })}
                                                curve={curveCardinal}
                                                x={d => xScale(d.Month.valueOf())}
                                                y={d => yScale(d.YoYChange)}
                                                stroke={BaseColor}
                                                strokeWidth={1.5}
                                                style={xAxisSlide(motionVar, (CurrentStep === "YoYMarketRegion"))}
                                                className={"lineStyle"}
                                                opacity={lineOpacity}
                                            />

                                        </Group>}
                                </Motion>

                                <Motion style={{ motionVar: spring((CurrentStep === "YoYMarketRegion" && ActiveFilters.includes("Markets")) ? 1 : 0) }}>
                                    {({ motionVar }) =>
                                        <Group id="YoyChangeMarketRegions">
                                            <LinePath
                                                data={YoYMarketRegionData.filter((d: YoYMarketRegion) => { return (d.Market === "Commercial" && d.Region === "Metropolitan") })}
                                                curve={curveCardinal}
                                                x={d => xScale(d.Month.valueOf())}
                                                y={d => yScale(d.YoYChange)}
                                                stroke={SMEColor}
                                                strokeWidth={1.5}
                                                className={"lineStyle"}
                                                opacity={motionVar}
                                            />
                                            <LinePath
                                                data={YoYMarketRegionData.filter((d: YoYMarketRegion) => { return (d.Market === "Large" && d.Region === "Metropolitan") })}
                                                curve={curveCardinal}
                                                x={d => xScale(d.Month.valueOf())}
                                                y={d => yScale(d.YoYChange)}
                                                stroke={LargeColor}
                                                strokeWidth={1.5}
                                                className={"lineStyle"}
                                                opacity={motionVar}
                                            />
                                            <LinePath
                                                data={YoYMarketRegionData.filter((d: YoYMarketRegion) => { return (d.Market === "Residential" && d.Region === "Metropolitan") })}
                                                curve={curveCardinal}
                                                x={d => xScale(d.Month.valueOf())}
                                                y={d => yScale(d.YoYChange)}
                                                stroke={ResidentialColor}
                                                strokeWidth={1.5}
                                                className={"lineStyle"}
                                                opacity={motionVar}
                                            />

                                            <LinePath
                                                data={YoYMarketRegionData.filter((d: YoYMarketRegion) => { return (d.Market === "Commercial" && d.Region === "Regional") })}
                                                curve={curveCardinal}
                                                x={d => xScale(d.Month.valueOf())}
                                                y={d => yScale(d.YoYChange)}
                                                stroke={SMEColor}
                                                strokeDasharray={"5, 3"}
                                                strokeWidth={1.5}
                                                className={"lineStyle"}
                                                opacity={motionVar}
                                            />
                                            <LinePath
                                                data={YoYMarketRegionData.filter((d: YoYMarketRegion) => { return (d.Market === "Large" && d.Region === "Regional") })}
                                                curve={curveCardinal}
                                                x={d => xScale(d.Month.valueOf())}
                                                y={d => yScale(d.YoYChange)}
                                                stroke={LargeColor}
                                                strokeDasharray={"5, 3"}
                                                strokeWidth={1.5}
                                                opacity={motionVar}
                                                className={"lineStyle"}
                                            />
                                            <LinePath
                                                data={YoYMarketRegionData.filter((d: YoYMarketRegion) => { return (d.Market === "Residential" && d.Region === "Regional") })}
                                                curve={curveCardinal}
                                                x={d => xScale(d.Month.valueOf())}
                                                y={d => yScale(d.YoYChange)}
                                                stroke={ResidentialColor}
                                                strokeWidth={1.5}
                                                strokeDasharray={"5, 3"}
                                                opacity={motionVar}
                                                className={"lineStyle"}
                                            />
                                        </Group>
                                    }
                                </Motion>
                            </Group>
                        </svg>
                    </Group>
                }
                {CurrentStep === "Calendar" &&
                    <Group id="CalendarGroup">
                        <KeyDateCalendar data={DailyChangeData} width={graphWidth} height={graphHeight} KeyDateData={KeyDateData} setStoryLim={SetStorylim} />
                    </Group>
                }
            </Grid>

            <Group id="NavButton" style={{ display: "flex", alignItems: "center", position: 'absolute', top: "50%", right: "0%" }}>
                {(CurrentStep !== "Calendar" && ActiveFilters.includes("Markets")) &&
                    <Fab color="primary" onClick={(e) => handleStep()}>
                        <NavigateNextIcon />
                    </Fab>
                }
                {(CurrentStep !== "Calendar" && CurrentStep !== "Intro" && !ActiveFilters.includes("Markets")) &&
                    <Tooltip title="Show Market Types to Continue">
                        <Fab onClick={(e) => activateMTFilter()}>
                            <NavigateNextIcon />
                        </Fab>
                    </Tooltip>
                }
            </Group>
            <Group id="IntroNavButton" style={{ display: "flex", alignItems: "center", position: 'absolute', bottom: "0%", right: "0%", flexDirection: 'column' }} className={"introNavButton"}>
                {(CurrentStep === "Intro") &&
                    <>
                        <Fab color="primary" onClick={(e) => handleStep()}>
                            <NavigateNextIcon />
                        </Fab>
                        <Typography variant="button">Next Visual</Typography>
                    </>
                }
            </Group>

            <Dialog
                open={showDialog}
                onClose={handleDialogClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">Thanks for Reading</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        The analysis done on this website has been a part of research into COVID-19 and electricity consumer trends for my university thesis. Part of this analysis involves evaluating the visuals shown thus far.
                    </DialogContentText>
                    <DialogContentText>
                        Below is a link to a very short anonymous survey about your experience with this site. It would greatly benefit my research into energy and visualisation if you could take the time to fill it in before moving on.
                    </DialogContentText>
                    <DialogContentText>
                        <Link href="https://forms.gle/Mo5EbTSWKYZWAcBM8" >Survey Link</Link>
                    </DialogContentText>
                    <DialogContentText>
                        For more information about this research please do not hesitate to reach out to me at sche259@student.monash.edu
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} color="primary" autoFocus>
                        Final Visual
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid >
    )
}